import { useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Link,
  makeStyles,
  Snackbar,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "@material-ui/core";
import {
  CheckCircle,
  Schedule,
  Error,
  HourglassEmpty,
  Person,
  CalendarToday,
  GetApp,
  Visibility,
  Description,
  Business,
  ThumbUp,
  Sync,
  RemoveCircle,
  AccessTime,
  Warning,
  DeleteOutline,
  DeleteSweep,
  ExpandMore as ExpandMoreIcon,
  ChevronRight,
  FileCopyOutlined,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { BoxRenderResult } from "./RenderHTML";
import JSONViewer from "./JSONViewer";
import PopUp from "./PopUp";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { Memory } from "@material-ui/icons";
import FileDownload from "./FileDownload";
import JobActionsCell from "./JobActionsCell";
import JobDetailsDrawer from "./JobDetailsDrawer";
import { getApiBaseUrl } from "../config";
import { requiresHumanAction } from "../utils/jobTaskStatus";
import { jobAllowsManualReprocess } from "../utils/jobReprocess";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    flex: 1,
    overflow: "auto",
    maxHeight: "100%",
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
  },
  tableBody: {},
  tableHeaderCell: {
    fontWeight: 600,
    fontSize: "0.72rem",
    color: "var(--ikaro-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderBottom: "1px solid var(--ikaro-border-default)",
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
  },
  /* Linhas com listras zebradas suaves e hover neutro (cinza/superfície
     em vez de laranja claro). O laranja ficava muito intenso quando havia
     muitas linhas; um cinza esfumaçado é mais agradável e funciona em
     light e dark sem ajuste extra (a variável já se adapta ao tema). */
  tableRow: {
    transition: "background-color 0.12s ease",
    "& > .MuiTableCell-root": {
      verticalAlign: "middle",
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      borderBottom: "1px solid var(--ikaro-border-light)",
    },
    "&:nth-of-type(even) > .MuiTableCell-root": {
      backgroundColor: "var(--ikaro-bg-surface-secondary)",
    },
    "&:hover > .MuiTableCell-root": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  statusChip: {
    fontSize: "0.72rem",
    minHeight: "22px",
    height: "auto",
    minWidth: "72px",
    maxWidth: "220px",
    fontWeight: 500,
    border: "1px solid transparent",
    "& .MuiChip-label": {
      whiteSpace: "normal",
      lineHeight: 1.25,
      paddingTop: "2px",
      paddingBottom: "2px",
      paddingLeft: "8px",
      paddingRight: "10px",
      textAlign: "center",
    },
    "& .MuiChip-icon": {
      marginLeft: "6px",
      marginRight: "-2px",
    },
  },
  statusIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(0.5),
  },
  actionButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  actionsCell: {
    width: "1%",
    whiteSpace: "nowrap",
  },
  /* Aplicada quando a coluna pede `align: "center"`. Centraliza tanto o
     conteúdo direto (texto) quanto wrappers de flex (linha — Box do status,
     coluna — container do ContestacaoOutputDisplay). */
  cellAlignCenter: {
    textAlign: "center",
    "& > div": {
      justifyContent: "center",
      alignItems: "center",
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  cellContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  fileInfo: {
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#666666",
  },
  // Inferências popup styles
  inferencesModalClearAllRow: {
    marginBottom: theme.spacing(1.5),
  },
  inferencesClearAllButton: {
    width: "100%",
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: "var(--ikaro-status-error) !important",
    color: "var(--ikaro-text-white) !important",
    "&:hover": {
      backgroundColor: "#d32f2f !important",
    },
    "&:disabled": {
      backgroundColor: "var(--ikaro-gray-medium) !important",
      color: "var(--ikaro-text-secondary) !important",
    },
  },
  inferencesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  inferencesCost: {
    fontWeight: "bold",
    color: "var(--ikaro-text-primary)",
  },
  inferencesContainer: {
    display: "flex",
    height: "60vh",
    minHeight: 300,
  },
  inferencesList: {
    overflowY: "auto",
    borderRight: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-inference-modal-list-bg)",
  },
  inferencesListItem: {
    padding: theme.spacing(1),
    cursor: "pointer",
    borderBottom: "1px solid var(--ikaro-border-default)",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  inferencesListItemActive: {
    backgroundColor: "var(--ikaro-purple-light)",
  },
  splitter: {
    width: 6,
    cursor: "col-resize",
    backgroundColor: "var(--ikaro-border-default)",
  },
  inferencesDetail: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: "var(--ikaro-inference-modal-detail-bg)",
  },
  inferencesDetailClearBar: {
    flexShrink: 0,
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-inference-modal-detail-toolbar-bg)",
  },
  inferencesDetailClearThisButton: {
    textTransform: "none",
    fontWeight: 600,
  },
  inferencesDetailScroll: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
    minHeight: 0,
  },
  inferenceExpansionPanel: {
    marginBottom: theme.spacing(1.5),
    boxShadow: "none",
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 4,
    backgroundColor: "var(--ikaro-bg-elevated)",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: theme.spacing(0, 0, 1.5, 0),
    },
  },
  inferenceExpansionSummary: {
    minHeight: 44,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    "& .MuiExpansionPanelSummary-content": {
      margin: theme.spacing(1.25, 0),
      justifyContent: "flex-start",
    },
  },
  inferenceExpansionDetails: {
    display: "block",
    textAlign: "left",
    padding: theme.spacing(0, 2, 2),
    borderTop: "1px solid var(--ikaro-border-default)",
    "& .MuiTypography-root": {
      textAlign: "left",
    },
  },
  inferenceExpansionTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: "var(--ikaro-text-primary)",
  },
  inferenceExpansionInner: {
    width: "100%",
    textAlign: "left",
  },
  panel: {
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "var(--ikaro-bg-elevated)",
    textAlign: "left",
  },
  panelTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: "var(--ikaro-text-secondary)",
  },
  monospaced: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: "left",
  },
  // Error popup styles
  errorContent: {
    maxHeight: "60vh",
    overflow: "auto",
    padding: theme.spacing(2),
    backgroundColor: "var(--ikaro-status-error-bg)",
    border: "1px solid var(--ikaro-status-error)",
    borderRadius: 4,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.875rem",
  },
  errorTitle: {
    color: "var(--ikaro-status-warning-text)",
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  eyeIcon: {
    fontSize: "1rem",
    color: "var(--ikaro-status-warning-text)",
    cursor: "pointer",
  },
  /* Botão de reexecutar (admin) inline na coluna de status, ao lado do
     "olho" de visualizar erro. Fica em destaque (laranja) para indicar
     uma ação executiva — distinguindo do olho, que é só consulta. */
  inlineReprocessButton: {
    padding: theme.spacing(0.25),
    marginLeft: theme.spacing(0.25),
    color: "var(--ikaro-orange-primary)",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      color: "var(--ikaro-orange-hover)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.05rem",
    },
  },
  resultLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    color: "var(--ikaro-orange-primary)",
    fontSize: "0.75rem",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
      color: "var(--ikaro-orange-hover)",
    },
  },
  resultIcon: {
    fontSize: "1rem",
    color: "var(--ikaro-orange-primary)",
  },
  /* Coluna fixa de "expandir": fica colada à direita com fundo igual ao da
     célula da linha (par/ímpar/hover) para parecer parte da própria linha. */
  expandHeaderCell: {
    width: 44,
    paddingLeft: 0,
    paddingRight: theme.spacing(0.5),
  },
  expandCell: {
    width: 44,
    paddingLeft: 0,
    paddingRight: theme.spacing(0.5),
    textAlign: "right",
  },
  expandButton: {
    padding: 6,
    color: "var(--ikaro-text-muted)",
    transition: "background-color 0.12s ease, color 0.12s ease",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      color: "var(--ikaro-orange-primary)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
  expandButtonActive: {
    backgroundColor: "var(--ikaro-orange-light)",
    color: "var(--ikaro-orange-primary)",
  },
  /* Célula de ID com ícone de copiar. Mantém o ID em fonte monoespaçada
     (mais fácil de ler/identificar) e o ícone discreto à direita. */
  idCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  idText: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "0.78rem",
    color: "var(--ikaro-text-primary)",
    fontWeight: 600,
  },
  idCopyButton: {
    padding: 2,
    color: "var(--ikaro-text-muted)",
    "&:hover": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "0.95rem",
    },
  },
}));

/* Copia para clipboard usando a API moderna quando disponível, com fallback
   para `document.execCommand("copy")` em contextos sem `navigator.clipboard`. */
const copyToClipboard = async (text) => {
  if (text == null) return false;
  const value = String(text);
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_err) {
    /* tenta fallback abaixo */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_err) {
    return false;
  }
};

/* Botão de copiar inline para a célula de ID. Mantemos como subcomponente
   para preservar o estado "copiado" sem re-renderizar a tabela inteira. */
const CopyIdButton = ({ value, classes, tooltipCopy, tooltipCopied }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = async (e) => {
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <Tooltip
      title={copied ? tooltipCopied : tooltipCopy}
      arrow
      placement="top"
    >
      <IconButton
        size="small"
        className={classes.idCopyButton}
        onClick={handleClick}
        aria-label={tooltipCopy}
      >
        {copied ? (
          <CheckCircleIcon style={{ color: "var(--ikaro-status-success)" }} />
        ) : (
          <FileCopyOutlined />
        )}
      </IconButton>
    </Tooltip>
  );
};

const JobTable = ({
  jobs,
  columns,
  customActions,
  sortConfig,
  onSort,
  onJobAction,
  emptyStateMessage,
  hideDefaultActions = false,
  hideViewAction = false,
  selectionMode = false,
  selectedIds = [],
  onSelectionChange = () => {},
  /* Quando definida, habilita a coluna ">" à direita de cada linha que abre
     o drawer lateral com os detalhes completos do job. A função recebe o
     job e devolve o conteúdo a ser renderizado dentro do drawer. */
  renderJobDetails = null,
  /* Título exibido no cabeçalho do drawer (opcional). Por padrão usa o
     nome do job ou a label genérica do drawer. */
  drawerTitle = null,
}) => {
  const classes = useStyles();
  const { t, formatDateTime } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");
  const { token, user, hasRole } = useAuth();
  const [drawerJob, setDrawerJob] = useState(null);
  const drawerEnabled = typeof renderJobDetails === "function";
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [contentPopUp, setContentPopUp] = useState("");
  // Error popup state
  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorContent, setErrorContent] = useState("");
  // Inferências state
  const [inferencesOpen, setInferencesOpen] = useState(false);
  const [inferencesLoading, setInferencesLoading] = useState(false);
  const [inferencesError, setInferencesError] = useState(null);
  const [inferencesData, setInferencesData] = useState({
    total_cost: 0,
    inferences: [],
  });
  const [selectedInferenceHash, setSelectedInferenceHash] = useState(null);
  const [inferenceDetailsLoading, setInferenceDetailsLoading] = useState(false);
  const [inferenceDetails, setInferenceDetails] = useState(null);
  const [leftPaneWidth, setLeftPaneWidth] = useState(150);
  const [inferencesJob, setInferencesJob] = useState(null);
  const [invalidateSingleCacheLoading, setInvalidateSingleCacheLoading] =
    useState(false);
  const [invalidateAllCacheLoading, setInvalidateAllCacheLoading] =
    useState(false);
  const [cacheSuccessMessage, setCacheSuccessMessage] = useState(null);
  const API_BASE_URL = getApiBaseUrl();

  const handleViewDetails = (
    output_payload,
    raw_result = null,
    input_payload = null,
  ) => {
    if (!output_payload) {
      setContentPopUp(tCommon("noResultsAvailable"));
      setPopUpOpen(true);
      return;
    }

    // Passa o raw_result de output_params se disponível
    // O BoxRenderResult só mostra a opção "mostrar resultado bruto" quando rawResult é passado
    // Também passa o input_payload como templateData para permitir visualizar detalhes dos campos
    setContentPopUp(
      <BoxRenderResult
        result={output_payload}
        rawResult={raw_result}
        templateData={input_payload}
      />,
    );
    setPopUpOpen(true);
  };

  const handleViewJson = (jsonData) => {
    if (!jsonData) {
      setContentPopUp(tCommon("noJsonAvailable"));
      setPopUpOpen(true);
      return;
    }
    setContentPopUp(<JSONViewer data={jsonData} />);
    setPopUpOpen(true);
  };

  const handleViewError = (errorDescription) => {
    if (!errorDescription) {
      setErrorContent(t("table.noErrorAvailable"));
    } else {
      setErrorContent(errorDescription);
    }
    setErrorPopupOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "DONE":
        return <CheckCircle className={classes.statusIcon} />;
      case "RUNNING":
        return <Schedule className={classes.statusIcon} />;
      case "ERROR":
      case "FINAL_ERROR":
        return <Error className={classes.statusIcon} />;
      case "WAITING":
        return <HourglassEmpty className={classes.statusIcon} />;
      case "PENDING_APPROVAL":
        return <AccessTime className={classes.statusIcon} />;
      case "APPROVED":
        return <ThumbUp className={classes.statusIcon} />;
      case "FINALIZING":
        return <Sync className={classes.statusIcon} />;
      case "DISCARDED":
        return <RemoveCircle className={classes.statusIcon} />;
      default:
        return <HourglassEmpty className={classes.statusIcon} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DONE":
        return {
          backgroundColor: "var(--ikaro-status-success-bg)",
          color: "var(--ikaro-status-success-text)",
        };
      case "RUNNING":
        return {
          backgroundColor: "var(--ikaro-status-running-bg)",
          color: "var(--ikaro-status-running-text)",
        };
      case "ERROR":
      case "FINAL_ERROR":
        return {
          backgroundColor: "var(--ikaro-status-warning-bg)",
          color: "var(--ikaro-status-warning-text)",
        };
      case "WAITING":
        return {
          backgroundColor: "var(--ikaro-status-warning-bg)",
          color: "var(--ikaro-status-warning-text)",
        };
      case "PENDING_APPROVAL":
        return {
          backgroundColor: "var(--ikaro-status-warning-bg)",
          color: "var(--ikaro-status-warning-text)",
        };
      case "APPROVED":
        return {
          backgroundColor: "var(--ikaro-status-success-bg)",
          color: "var(--ikaro-status-success-text)",
        };
      case "FINALIZING":
        return {
          backgroundColor: "var(--ikaro-status-running-bg)",
          color: "var(--ikaro-status-running-text)",
        };
      case "DISCARDED":
        return {
          backgroundColor: "var(--ikaro-gray-light)",
          color: "var(--ikaro-gray-dark)",
        };
      default:
        return {
          backgroundColor: "var(--ikaro-gray-light)",
          color: "var(--ikaro-gray-darker)",
        };
    }
  };

  const formatDate = (dateString) =>
    formatDateTime(dateString, { emptyValue: "-" });

  const openInferences = async (job) => {
    if (!API_BASE_URL || !token || !job?.id) return;
    try {
      setInferencesError(null);
      setInferencesLoading(true);
      setInferencesOpen(true);
      setInferencesJob(job);
      setInferenceDetails(null);
      setSelectedInferenceHash(null);

      const url = `${API_BASE_URL}/job/get_job_inferences?job_id=${encodeURIComponent(
        job.id,
      )}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferencesData({
        total_cost: data.total_cost || 0,
        inferences: Array.isArray(data.inferences) ? data.inferences : [],
      });
    } catch (err) {
      setInferencesError(err.message || "Erro ao carregar inferências");
    } finally {
      setInferencesLoading(false);
    }
  };

  const fetchInferenceDetails = async (hash) => {
    if (!API_BASE_URL || !token || !hash) return;
    try {
      setInferenceDetailsLoading(true);
      setInferenceDetails(null);
      const url = `${API_BASE_URL}/job/get_inference_details?hash=${encodeURIComponent(
        hash,
      )}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setInferenceDetails(data);
    } catch (err) {
      setInferenceDetails({});
    } finally {
      setInferenceDetailsLoading(false);
    }
  };

  const firstTagOfInference = (inf) => {
    if (!inf) return "-";
    if (inf.input && inf.input.tag) return inf.input.tag;
    if (inf.output && inf.output.tag) return inf.output.tag;
    if (inf.embedding && inf.embedding.tag) return inf.embedding.tag;
    return "-";
  };

  const inferenceHashSha256Re = /^[a-fA-F0-9]{64}$/;
  const isValidInferenceHash = (h) =>
    Boolean(h && inferenceHashSha256Re.test(String(h).trim()));

  const handleInvalidateCacheForSelectedHash = useCallback(async () => {
    if (!API_BASE_URL || !token) return;
    if (!isValidInferenceHash(selectedInferenceHash)) {
      setErrorContent(t("inferences.selectInferenceFirst"));
      setErrorPopupOpen(true);
      return;
    }
    setInvalidateSingleCacheLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/invalidate_inference_cache`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ hash: String(selectedInferenceHash).trim() }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || t("inferences.cacheClearError"));
      }
      setCacheSuccessMessage(t("inferences.cacheClearedSuccess"));
    } catch (err) {
      setErrorContent(err.message || t("inferences.cacheClearError"));
      setErrorPopupOpen(true);
    } finally {
      setInvalidateSingleCacheLoading(false);
    }
  }, [API_BASE_URL, token, selectedInferenceHash, t]);

  const handleInvalidateAllCachesForJob = useCallback(async () => {
    if (!API_BASE_URL || !token || !inferencesJob?.id) return;
    setInvalidateAllCacheLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/job/invalidate_inference_cache`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ job_id: inferencesJob.id }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || t("inferences.cacheClearError"));
      }
      setCacheSuccessMessage(t("inferences.cacheClearedSuccess"));
    } catch (err) {
      setErrorContent(err.message || t("inferences.cacheClearError"));
      setErrorPopupOpen(true);
    } finally {
      setInvalidateAllCacheLoading(false);
    }
  }, [API_BASE_URL, token, inferencesJob, t]);

  const handleMouseDownOnSplitter = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPaneWidth;

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const newWidth = Math.max(120, Math.min(startWidth + dx, 600));
      setLeftPaneWidth(newWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const renderInferenceRightPanel = () => {
    const collapsibleSection = (key, title, inner) => (
      <ExpansionPanel
        key={key}
        className={classes.inferenceExpansionPanel}
        defaultExpanded={false}
      >
        <ExpansionPanelSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.inferenceExpansionSummary}
        >
          <Typography className={classes.inferenceExpansionTitle}>
            {title}
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.inferenceExpansionDetails}>
          {inner}
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );

    if (inferenceDetailsLoading) {
      return <Typography variant="body2">{tCommon("loading")}...</Typography>;
    }
    if (!inferenceDetails || Object.keys(inferenceDetails).length === 0) {
      return (
        <Typography variant="body2" className={classes.monospaced}>
          {t("inferences.noResultForHash", {
            hash: selectedInferenceHash || "-",
          })}
        </Typography>
      );
    }

    if (inferenceDetails.type === "chat") {
      const inputTokens = inferenceDetails.costs?.input ?? 0;
      const outputTokens = inferenceDetails.costs?.output ?? 0;
      const systemMessage = inferenceDetails.system_message;
      const userMessage = inferenceDetails.user_message;
      const result = inferenceDetails.result;
      const pdfFile = inferenceDetails.pdf; // { filename, token, html_preview_token? }
      const imageBase64 = inferenceDetails.image_base64;
      const imageMediaType = inferenceDetails.image_media_type;
      const costRow = (inferencesData.inferences || []).find(
        (row) => row?.hash === selectedInferenceHash,
      );
      const costUsd =
        costRow != null &&
        costRow.cost_usd != null &&
        costRow.cost_usd !== ""
          ? Number(costRow.cost_usd)
          : null;

      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t("inferences.chatTitle")}
          </Typography>
          {collapsibleSection(
            "consumption",
            t("inferences.consumption"),
            <Box className={classes.inferenceExpansionInner}>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.inputTokens")}: {inputTokens}
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.outputTokens")}: {outputTokens}
              </Typography>
              {costUsd != null && !Number.isNaN(costUsd) && (
                <Typography variant="body2" color="textPrimary">
                  {t("inferences.individualCostUsd")}: USD{" "}
                  {costUsd.toFixed(6)}
                </Typography>
              )}
            </Box>,
          )}
          {systemMessage &&
            collapsibleSection(
              "system",
              t("inferences.systemMessage"),
              <Typography variant="body2" className={classes.monospaced}>
                {systemMessage}
              </Typography>,
            )}
          {userMessage &&
            collapsibleSection(
              "user",
              t("inferences.userMessage"),
              <Typography variant="body2" className={classes.monospaced}>
                {userMessage}
              </Typography>,
            )}
          {imageBase64 &&
            imageMediaType &&
            collapsibleSection(
              "image",
              t("inferences.image"),
              <img
                alt="inference"
                src={`data:${imageMediaType};base64,${imageBase64}`}
                style={{ maxWidth: "100%" }}
              />,
            )}
          {pdfFile &&
            pdfFile.filename &&
            collapsibleSection(
              "file",
              t("inferences.file"),
              <FileDownload file={pdfFile} showError={false} />,
            )}
          {result &&
            collapsibleSection(
              "result",
              t("inferences.result"),
              <Typography variant="body2" className={classes.monospaced}>
                {result}
              </Typography>,
            )}
        </Box>
      );
    }

    if (inferenceDetails.type === "embedding") {
      const tokens = inferenceDetails.costs?.tokens ?? 0;
      const dimension = inferenceDetails.dimension ?? "-";
      const text = inferenceDetails.text;
      const vector = Array.isArray(inferenceDetails.result)
        ? inferenceDetails.result
        : [];
      const costRowEmb = (inferencesData.inferences || []).find(
        (row) => row?.hash === selectedInferenceHash,
      );
      const costUsdEmb =
        costRowEmb != null &&
        costRowEmb.cost_usd != null &&
        costRowEmb.cost_usd !== ""
          ? Number(costRowEmb.cost_usd)
          : null;

      const prettyVector = (() => {
        const parts = [];
        const perLine = 8;
        for (let i = 0; i < vector.length; i += perLine) {
          const slice = vector
            .slice(i, i + perLine)
            .map((n) => (typeof n === "number" ? n.toFixed(9) : String(n)));
          parts.push(`  ${slice.join(", ")}`);
        }
        return `[
${parts.join("\n")}
]`;
      })();

      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t("inferences.embeddingsTitle")}
          </Typography>
          {collapsibleSection(
            "consumption",
            t("inferences.consumption"),
            <Box className={classes.inferenceExpansionInner}>
              <Typography variant="body2" color="textPrimary">
                {t("inferences.tokens")}: {tokens}
              </Typography>
              {costUsdEmb != null && !Number.isNaN(costUsdEmb) && (
                <Typography variant="body2" color="textPrimary">
                  {t("inferences.individualCostUsd")}: USD{" "}
                  {costUsdEmb.toFixed(6)}
                </Typography>
              )}
            </Box>,
          )}
          {collapsibleSection(
            "dimension",
            t("inferences.dimensionTitle"),
            <Typography variant="body2" color="textPrimary">
              {t("inferences.dimension")}: {dimension}
            </Typography>,
          )}
          {text &&
            collapsibleSection(
              "text",
              t("inferences.text"),
              <Typography variant="body2" className={classes.monospaced}>
                {text}
              </Typography>,
            )}
          {collapsibleSection(
            "vector",
            t("inferences.result"),
            <Typography variant="body2" className={classes.monospaced}>
              {prettyVector}
            </Typography>,
          )}
        </Box>
      );
    }

    return null;
  };

  const renderCellContent = (column, job) => {
    const value = job[column.key];

    switch (column.type) {
      case "id": {
        if (value == null || value === "") {
          return (
            <Typography variant="body2" className="text-muted">
              -
            </Typography>
          );
        }
        return (
          <Box className={classes.idCell}>
            <Tooltip title={`${t("table.jobId")}: ${value}`} arrow>
              <Typography component="span" className={classes.idText}>
                #{value}
              </Typography>
            </Tooltip>
            <CopyIdButton
              value={value}
              classes={classes}
              tooltipCopy={tCommon("copyId")}
              tooltipCopied={tCommon("copied")}
            />
          </Box>
        );
      }

      case "status":
        const isPendingUser =
          value?.toUpperCase() === "PENDING" &&
          job.token_expiration_timestamp != null;
        const isTokenExpired =
          isPendingUser &&
          new Date(job.token_expiration_timestamp) < new Date();
        const needsHumanAction = requiresHumanAction(job);
        const statusChipClassName = needsHumanAction
          ? `${classes.statusChip} ikaro-status-chip-action-required`
          : classes.statusChip;
        /* Quando a tag está em destaque (ação humana), as cores vêm da
           classe CSS para inverter a paleta corretamente; nos demais casos,
           seguimos com a cor inline padrão por status. */
        const statusChipStyle = needsHumanAction
          ? undefined
          : getStatusColor(value);
        return (
          <Box className={classes.cellContent}>
            <Chip
              icon={getStatusIcon(value)}
              label={column.formatValue ? column.formatValue(value, job) : value}
              className={statusChipClassName}
              style={statusChipStyle}
            />
            {/* Ícone: link de ativação/redefinição expirado (usuário pendente) */}
            {isTokenExpired && (
              <Tooltip title={t("table.tokenExpiredTooltip")}>
                <Warning
                  style={{
                    fontSize: "1.25rem",
                    color: "var(--ikaro-status-warning-text)",
                  }}
                />
              </Tooltip>
            )}
            {/* Ícone de olho para visualizar erro se o usuário tem permissão e o job está em análise */}
            {user &&
              user.permissions &&
              hasRole("ADMIN", "job_error_viewer") &&
              (value?.toUpperCase() === "ERROR" ||
                value?.toUpperCase() === "FINAL_ERROR") &&
              job.error_description && (
                <Tooltip title={t("table.viewError")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewError(job.error_description);
                    }}
                    className={classes.actionButton}
                  >
                    <Visibility className={classes.eyeIcon} />
                  </IconButton>
                </Tooltip>
              )}
            {/* Botão admin: reexecutar job em erro. Aparece colado ao chip
                de status (e ao "olho" de erro, quando presente) para que o
                administrador encontre a ação no mesmo lugar em que está
                consultando o problema. Dispara onJobAction("reprocess"),
                que cada página trata para chamar PATCH /job/:id/reprocess. */}
            {user &&
              user.permissions &&
              hasRole("ADMIN", "re_execute_job") &&
              jobAllowsManualReprocess(job) && (
                <Tooltip title={t("actions.reprocessTooltip")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onJobAction && onJobAction("reprocess", job);
                    }}
                    className={classes.inlineReprocessButton}
                    aria-label={t("actions.primary.retry")}
                  >
                    <Sync />
                  </IconButton>
                </Tooltip>
              )}
          </Box>
        );

      case "date":
        return (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {t("table.jobDetailsTitle")}
                </Typography>
                <Typography variant="body2">
                  {t("table.jobId")}: {job.id || "-"}
                </Typography>
                <Typography variant="body2">
                  {t("table.externalId")}: {job.external_id || "-"}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box className={`${classes.cellContent} cursor-help`}>
              <CalendarToday
                style={{ fontSize: "1rem", color: "var(--ikaro-gray-dark)" }}
              />
              <Typography variant="body2">{formatDate(value)}</Typography>
              {user &&
                user.permissions &&
                hasRole("ADMIN", "inferences_viewer") && (
                  <Tooltip title={t("inferences.viewInferences")}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInferences(job);
                      }}
                    >
                      <Memory
                        style={{
                          fontSize: "1rem",
                          color: "var(--ikaro-purple-accent)",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
          </Tooltip>
        );

      case "user":
        return (
          <Box className={classes.cellContent}>
            <Person
              style={{ fontSize: "1rem", color: "var(--ikaro-gray-dark)" }}
            />
            <Typography variant="body2">{value || "-"}</Typography>
          </Box>
        );

      case "files":
        return (
          <Box>
            {job.arquivo_pdf && (
              <Box className={classes.fileInfo}>
                <Description style={{ fontSize: "0.875rem" }} />
                <Typography variant="caption">{job.arquivo_pdf}</Typography>
              </Box>
            )}
            {job.template && (
              <Box className={classes.fileInfo}>
                <Description style={{ fontSize: "0.875rem" }} />
                <Typography variant="caption">{job.template}</Typography>
              </Box>
            )}
            {job.input_payload && (
              <Button
                onClick={() => handleViewJson(job.input_payload)}
                startIcon={<Visibility style={{ fontSize: "1rem" }} />}
              >
                {t("table.viewInput")}
              </Button>
            )}
          </Box>
        );

      case "outputs":
        /* Quando há resultado, mostra de forma compacta. Sem resultado,
           devolve um traço discreto — o status na coluna de Status já
           comunica em qual etapa o job está, sem precisar repetir
           "Não disponível" para cada linha. */
        return job.resultados || job.output_payload ? (
          <Box className={classes.cellContent}>
            {job.resultados && (
              <>
                <GetApp
                  style={{
                    fontSize: "1rem",
                    color: "var(--ikaro-status-success)",
                  }}
                />
                <Typography
                  variant="body2"
                  style={{ color: "var(--ikaro-status-success)" }}
                >
                  {job.resultados || t("common:available")}
                </Typography>
              </>
            )}
            {job.output_payload && (
              <Link
                component="button"
                className={classes.resultLink}
                onClick={() =>
                  handleViewDetails(
                    job.output_payload,
                    job.output_params?.raw_result,
                    job.input_payload,
                  )
                }
              >
                <Visibility className={classes.resultIcon} />
                {t("table.result")}
              </Link>
            )}
          </Box>
        ) : (
          <Typography
            component="span"
            style={{
              color: "var(--ikaro-text-muted)",
              fontSize: "0.95rem",
              userSelect: "none",
            }}
          >
            —
          </Typography>
        );

      case "external_id":
        if (!value) return "-";
        const shortId =
          value.length > 5 ? `${value.substring(0, 5)}...` : value;
        return (
          <Tooltip title={`${t("table.externalId")}: ${value}`} arrow>
            <Typography
              variant="body2"
              className="cursor-help"
              style={{ color: "var(--ikaro-status-running)" }}
            >
              {shortId}
            </Typography>
          </Tooltip>
        );

      case "cell":
        return (
          <Chip
            icon={<Business style={{ fontSize: "0.875rem" }} />}
            label={value}
            size="small"
            style={{
              backgroundColor: "var(--ikaro-gray-light)",
              color: "var(--ikaro-gray-darker)",
            }}
          />
        );

      case "actions": {
        /* Compõe a lista final de ações combinando as padrão (quando habilitadas)
           com as customizadas vindas do callsite. Cada ação carrega seu `tier`
           ("primary" | "secondary" | "menu" | "danger") para que
           `JobActionsCell` decida a renderização. */
        const builtInActions = [];
        if (!hideDefaultActions) {
          if (!hideViewAction) {
            builtInActions.push({
              key: "view",
              tier: "secondary",
              tooltip: t("table.viewDetails"),
              icon: <Visibility />,
            });
          }
          if (job.resultados || job.output_payload) {
            builtInActions.push({
              key: "download",
              tier: "primary",
              label: t("actions.primary.download"),
              tooltip: t("table.downloadResults"),
              icon: <GetApp />,
            });
          }
        }
        const allActions = [...builtInActions, ...customActions];
        return (
          <JobActionsCell
            job={job}
            actions={allActions}
            onAction={onJobAction}
          />
        );
      }

      case "custom":
        // Suporte para células customizadas
        if (column.renderCell) {
          return column.renderCell(job);
        }
        return (
          <Typography variant="body2">
            {column.formatValue ? column.formatValue(value, job) : value || "-"}
          </Typography>
        );

      default:
        return (
          <Typography variant="body2">
            {column.formatValue ? column.formatValue(value, job) : value || "-"}
          </Typography>
        );
    }
  };

  const handleSort = (column) => {
    if (column.sortable !== false) {
      onSort && onSort(column.key);
    }
  };

  if (jobs.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          {emptyStateMessage || t("table.emptyState")}
        </Typography>
        <Typography variant="body2">
          {t("table.emptyStateDescription")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer className={classes.tableContainer}>
      {/* Popup existente */}
      <PopUp
        open={popUpOpen}
        onClose={() => setPopUpOpen(false)}
        title={""}
        children={contentPopUp}
      />

      {/* Popup de Erro */}
      <PopUp
        open={errorPopupOpen}
        onClose={() => setErrorPopupOpen(false)}
        title=""
      >
        <Box>
          <Typography variant="h6" className={classes.errorTitle}>
            {t("table.errorDetailsTitle")}
          </Typography>
          <Box className={classes.errorContent}>{errorContent}</Box>
        </Box>
      </PopUp>

      {/* Popup de Inferências */}
      <PopUp
        open={inferencesOpen}
        onClose={() => {
          setInferencesOpen(false);
          setInferencesJob(null);
        }}
        title={""}
      >
        <Box className={classes.inferencesModalClearAllRow}>
          <Button
            variant="contained"
            size="small"
            className={classes.inferencesClearAllButton}
            disabled={
              invalidateAllCacheLoading ||
              invalidateSingleCacheLoading ||
              !inferencesJob?.id
            }
            startIcon={
              invalidateAllCacheLoading ? (
                <CircularProgress size={18} style={{ color: "inherit" }} />
              ) : (
                <DeleteSweep fontSize="small" />
              )
            }
            onClick={handleInvalidateAllCachesForJob}
          >
            {t("inferences.clearCacheAllJob")}
          </Button>
        </Box>
        <Box className={classes.inferencesHeader}>
          <Typography variant="h6" style={{ color: "var(--ikaro-text-primary)" }}>
            {t("inferences.title")}
          </Typography>
          <Typography variant="body2" className={classes.inferencesCost}>
            {t("inferences.totalCost")}: USD{" "}
            {Number(inferencesData.total_cost || 0).toFixed(6)}
          </Typography>
        </Box>
        <Box className={classes.inferencesContainer}>
          <Box
            className={classes.inferencesList}
            style={{ width: leftPaneWidth }}
          >
            {inferencesLoading ? (
              <Typography variant="body2">{tCommon("loading")}...</Typography>
            ) : inferencesError ? (
              <Typography variant="body2" color="error">
                {inferencesError}
              </Typography>
            ) : (
              (inferencesData.inferences || []).map((inf, idx) => {
                const hash = inf.hash;
                const label = `${idx + 1} - ${firstTagOfInference(inf)}`;
                const active = selectedInferenceHash === hash;
                return (
                  <Box
                    key={hash || idx}
                    className={`${classes.inferencesListItem} ${
                      active ? classes.inferencesListItemActive : ""
                    }`}
                    onClick={() => {
                      setSelectedInferenceHash(hash);
                      fetchInferenceDetails(hash);
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: active ? "bold" : "normal" }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
          <Box
            className={classes.splitter}
            onMouseDown={handleMouseDownOnSplitter}
          />
          <Box className={classes.inferencesDetail}>
            <Box className={classes.inferencesDetailClearBar}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                fullWidth
                className={classes.inferencesDetailClearThisButton}
                disabled={
                  invalidateSingleCacheLoading ||
                  invalidateAllCacheLoading ||
                  !isValidInferenceHash(selectedInferenceHash)
                }
                startIcon={
                  invalidateSingleCacheLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteOutline fontSize="small" />
                  )
                }
                onClick={handleInvalidateCacheForSelectedHash}
              >
                {t("inferences.clearCacheThis")}
              </Button>
            </Box>
            <Box className={classes.inferencesDetailScroll}>
              {renderInferenceRightPanel()}
            </Box>
          </Box>
        </Box>
      </PopUp>

      <Snackbar
        open={Boolean(cacheSuccessMessage)}
        autoHideDuration={4000}
        onClose={() => setCacheSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCacheSuccessMessage(null)}
          severity="success"
          elevation={6}
          variant="filled"
        >
          {cacheSuccessMessage}
        </Alert>
      </Snackbar>

      <Table className={classes.table} stickyHeader>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {selectionMode && (
              <TableCell padding="checkbox" className={classes.tableHeaderCell}>
                <Checkbox
                  indeterminate={
                    selectedIds.length > 0 && selectedIds.length < jobs.length
                  }
                  checked={
                    jobs.length > 0 && selectedIds.length === jobs.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectionChange(jobs.map((j) => j.id));
                    } else {
                      onSelectionChange([]);
                    }
                  }}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.key}
                className={`${classes.tableHeaderCell} ${
                  column.type === "actions" ? classes.actionsCell : ""
                } ${column.align === "center" ? classes.cellAlignCenter : ""}`}
                align={column.align || "inherit"}
                sortDirection={
                  sortConfig.sortBy === column.key
                    ? sortConfig.sortOrder
                    : false
                }
              >
                {column.sortable !== false ? (
                  <TableSortLabel
                    active={sortConfig.sortBy === column.key}
                    direction={
                      sortConfig.sortBy === column.key
                        ? sortConfig.sortOrder
                        : "asc"
                    }
                    onClick={() => handleSort(column)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            {drawerEnabled && (
              <TableCell
                className={`${classes.tableHeaderCell} ${classes.expandHeaderCell}`}
                aria-label={t("drawer.toggleAriaLabel")}
              />
            )}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {jobs.map((job, index) => (
            <TableRow key={job.id || index} className={classes.tableRow}>
              {selectionMode && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.indexOf(job.id) !== -1}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectionChange([...selectedIds, job.id]);
                      } else {
                        onSelectionChange(
                          selectedIds.filter((id) => id !== job.id),
                        );
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "inherit"}
                  className={`${
                    column.type === "actions" ? classes.actionsCell : ""
                  } ${
                    column.align === "center" ? classes.cellAlignCenter : ""
                  }`.trim()}
                >
                  {renderCellContent(column, job)}
                </TableCell>
              ))}
              {drawerEnabled && (
                <TableCell className={classes.expandCell}>
                  <Tooltip title={t("drawer.openTooltip")} arrow placement="left">
                    <IconButton
                      size="small"
                      className={`${classes.expandButton} ${
                        drawerJob?.id === job.id
                          ? classes.expandButtonActive
                          : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawerJob(job);
                      }}
                      aria-label={t("drawer.openTooltip")}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {drawerEnabled && (
        <JobDetailsDrawer
          open={Boolean(drawerJob)}
          onClose={() => setDrawerJob(null)}
          job={drawerJob}
          title={drawerTitle}
          renderContent={renderJobDetails}
        />
      )}
    </TableContainer>
  );
};

export default JobTable;
